import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Heading,
  Input,
  Select,
  Textarea,
  Text,
  VStack,
  HStack,
  Badge,
  Flex,
  IconButton,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  Tag,
  Switch,
  Center,
  Spinner
} from '@chakra-ui/react';
import ReactSelect from "react-select";
import {

  FiSave,

  FiDollarSign,
  FiActivity,
  FiTarget,

  FiPlus,
  FiTrash2,

  FiGlobe,
} from 'react-icons/fi';

import { ChannelTabsFixed } from './ChannelTabsFixed';
import Header from '../../../components/Header';
import { fetchWrapper } from '../../../helpers';

import { useNavigate, useParams } from 'react-router-dom';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import DeepLinkSelect from '../../../components/DeepLinkPicker/DeepLinkSelect';
import { COUNTRY_CODES, languageOptions, MESSAGE_EXPIRY_OPTIONS, getApiHeaders, hasPermission } from '../../../utils/DefaultFunction';
import { countEmojis } from '../../../utils/countEmojis';
import API from '../../../helpers/axiosInstance';
import { CHANNEL, getChannel } from '../../../utils/channels';




const countryCodes = [
  { code: '+1', country: 'US', flag: '🇺🇸', name: 'United States' },
  { code: '+44', country: 'GB', flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+91', country: 'IN', flag: '🇮🇳', name: 'India' },
  { code: '+92', country: 'PK', flag: '🇵🇰', name: 'Pakistan' },
  { code: '+971', country: 'AE', flag: '🇦🇪', name: 'UAE' },
  { code: '+966', country: 'SA', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '+49', country: 'DE', flag: '🇩🇪', name: 'Germany' },
  { code: '+33', country: 'FR', flag: '🇫🇷', name: 'France' },
  { code: '+39', country: 'IT', flag: '🇮🇹', name: 'Italy' },
  { code: '+34', country: 'ES', flag: '🇪🇸', name: 'Spain' }
];




/**
 * Authentication templates (templateType '1' — see MESSAGE_EXPIRY_OPTIONS) all carry the
 * same one-line body, so it is prefilled rather than retyped per channel.
 *
 * WhatsApp is a special case: Meta only approves an authentication template whose body is
 * this exact copy, with the security sentence appended when "Add security recommendation"
 * is on. So the WhatsApp field is prefilled AND locked, while SMS / Push / In-App / Voice
 * get the same text as a starting point and stay editable.
 */
const OTP_BODY = '$otp is your verification code.';
const OTP_BODY_WITH_SECURITY = '$otp is your verification code. For your security, do not share this code.';

/** True when a body is still the untouched prefill, so it is safe to replace or clear. */
const isOtpBody = (value) => {
  const text = String(value || '').trim();
  return text === OTP_BODY || text === OTP_BODY_WITH_SECURITY;
};

/**
 * Reads a stored on/off flag whichever way the API spells it — 1, '1', true, 'Y', 'yes'.
 * A plain truthiness check would read the string 'N' (and '0') as ON.
 */
const isFlagOn = (value) =>
  value === true ||
  value === 1 ||
  ['1', 'y', 'yes', 'true'].includes(String(value).trim().toLowerCase());

export default function TemplateManager({ isEdit }) {
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const permParams = "template-manager";

  const fetchPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
      const response = await API.get(url, { headers: getApiHeaders() });
      setPermissions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  };
  useEffect(() => {
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const xPermission = hasPermission(isEdit ? "template-edit" : "template-create", permissions);

  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateType, setTemplateType] = useState('1');
  const [description, setDescription] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);

  const [variables, setVariables] = useState([]);
  const [variableSampleValues, setVariableSampleValues] = useState({});

  const [whatsappBody, setWhatsappBody] = useState("");
  const [whatsappHeader, setWhatsappHeader] = useState({ type: null, content: '' });
  const [whatsappFooter, setWhatsappFooter] = useState('');
  const [whatsappButtons, setWhatsappButtons] = useState([]);
  const [whatsappMessageExpiry, setWhatsappMessageExpiry] = useState(() => MESSAGE_EXPIRY_OPTIONS['1']?.[0]?.value ?? 30);
  const [whatsappMessageExpiryEnabled, setWhatsappMessageExpiryEnabled] = useState(false);

  const [otpType, setOtpType] = useState("COPY_CODE");
  const [otpApps, setOtpApps] = useState([{ package_name: '', signature_hash: '' }]);

  const [inAppBody, setInAppBody] = useState('');
  const [inAppHeader, setInAppHeader] = useState({ type: null, content: '' });
  const [inAppFooter, setInAppFooter] = useState('');
  const [inAppButtons, setInAppButtons] = useState([]);
  const [inAppMessageExpiry, setInAppMessageExpiry] = useState('');
  const [inAppMessageExpiryEnabled, setInAppMessageExpiryEnabled] = useState(false);

  const [smsBody, setSmsBody] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushImage, setPushImage] = useState('');
  const [deeplink, setPushDeepLink] = useState('');

  const [emailSubject, setEmailSubject] = useState('');
  const [emailType, setEmailType] = useState('text');
  const [emailHtml, setEmailHtml] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('');
  const [emailTemplateId, setEmailTemplateId] = useState('');
  const [authCodeExpiryMinutes, setAuthCodeExpiryMinutes] = useState('1');
  const [enableTracker, setEnableTracker] = useState(0);
  // Sent as 0/1 in the create-combine payload, matching enable_tracker beside them.
  // Chargeable defaults on: a template bills unless it is explicitly marked free.
  const [chargeable, setChargeable] = useState(1);
  const [optInOut, setOptInOut] = useState(0);

  const [uploading, setUploading] = useState(false);
  const [authAddSecurityRecommendation, setAuthAddSecurityRecommendation] = useState(true);
  const [authAddExpirationTime, setAuthAddExpirationTime] = useState(false);

  const [voiceScript, setVoiceScript] = useState('');
  const [voiceRetries, setVoiceRetries] = useState('');
  const [voiceCallType, setVoiceCallType] = useState('text_to_voice'); // 'text_to_voice' | 'recorded_call'
  const [voiceCallId, setVoiceCallId] = useState('');
  const [voiceCallIds, setVoiceCallIds] = useState([]);
  const [voiceVariableSource, setVoiceVariableSource] = useState('from_channels'); // 'from_channels' | 'custom'
  const [ivrVariables, setIvrVariables] = useState([]);
  const [templateTypes, setMessageTemplateTypeList] = useState([]);
  const [lang, setLang] = useState("en");

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [webStoriesList, setWebStoriesList] = useState([]);
  const [webStoriesLoading, setWebStoriesLoading] = useState(false);

  const fetchWebStories = async () => {
    if (webStoriesList.length > 0) return;
    setWebStoriesLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/stories?status=published`, {
        headers: {
          'x-access-token': localStorage.getItem('user') || localStorage.getItem('customer-portal'),
          'x-api-key': localStorage.getItem('api-key'),
        },
      });
      setWebStoriesList(res.data?.data || []);
    } catch {
      setWebStoriesList([]);
    } finally {
      setWebStoriesLoading(false);
    }
  };
  const [events, setEvents] = useState([]);
  const { id } = useParams()
  const toast = useToast()
  const [whatsappStatusId, setWhatsappStatusid] = useState("")
  // Tab order: Whatsapp, SMS, Push, In-App, Voice, E-Mail
  const ALL_CHANNELS = [2, 1, 6, 5, 4, 3].map(channelId => getChannel(channelId));
  const [tabIndex, setTabIndex] = React.useState(0);
  const navigate = useNavigate()
  // 3. Add these state variables (add with your other useState declarations)
  const [customerList, setCustomerList] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [templateDataGlobal, settemplateData] = useState(null);





  // 4. Add this useEffect to load customer from localStorage (add after your existing useEffects)
  useEffect(() => {
    const customerData = localStorage.getItem("customer");
    if (customerData) {
      try {
        const customer = JSON.parse(customerData);
        const customerId = customer?.customer_id;

        if (customerId) {

          setCurrentCustomer(customerId);
          setSelectedCustomer(customerId);
        }
      } catch (error) {
        console.error('Error parsing customer data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load customer data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }

    // Load customers list
    LoadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 5. Add LoadCustomers function (add after LoadMessageTemplateType)
  const LoadCustomers = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/customer/list`;
      const data = await fetchWrapper.get(url);
      setCustomerList(data.data || []);

    } catch (error) {
      console.error('Error loading customers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load customers',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 6. Add helper function to get customer's available channels
  const getCustomerChannels = (customerId, customers = customerList) => {
    if (!customerId || !customers.length) {

      return [];
    }

    const customer = customers.find(c => c.id === customerId);
    if (!customer || !customer.customer_channel) {

      return [];
    }

    const customerChannels = ALL_CHANNELS.filter(channel =>
      customer.customer_channel.includes(channel.id)
    );


    return customerChannels;
  };

  // 7. Add useEffect to update available channels when customer loads
  useEffect(() => {
    if (currentCustomer && customerList.length > 0) {
      const customerChannels = getCustomerChannels(currentCustomer, customerList);
      setAvailableChannels(customerChannels);


      if (isEdit && selectedChannels.length > 0) {
        filterSelectedChannelsByAvailability(customerChannels);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCustomer, customerList]);

  // 8. Add function to filter selected channels by availability
  const filterSelectedChannelsByAvailability = (customerChannels) => {
    const availableChannelNames = customerChannels.map(ch => ch.name);

    const filteredSelectedChannels = selectedChannels.filter(
      channelName => availableChannelNames.includes(channelName)
    );

    if (filteredSelectedChannels.length !== selectedChannels.length) {


      setSelectedChannels(filteredSelectedChannels);

      const removedCount = selectedChannels.length - filteredSelectedChannels.length;
      toast({
        title: 'Channels Updated',
        description: `${removedCount} channel(s) removed as they are not available for this customer`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  };




  useEffect(() => {
    if (permissionsLoading || !xPermission) return;
    if (isEdit && id) {
      loadTemplateForEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, permissionsLoading, xPermission]);

  const loadTemplateForEdit = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/template-message/get-by-id?id=${id}`;
      const response = await fetchWrapper.get(url);

      if (response.data) {
        const templateData = response.data;

        settemplateData(templateData)
        if (templateData.message_combine) {
          try {
            const parsedData = JSON.parse(templateData.message_combine);
            setWhatsappStatusid(templateData?.whatsapp_status_id)
            setTemplateName(parsedData.templateName || '');
            setLang(parsedData.lang || '');
            setTemplateType(parsedData.templateType || '');
            setDescription(parsedData.description || '');
            setSelectedCustomer(parsedData.customer_id || '');
            setAuthAddExpirationTime(parsedData.authCodeExpiryMinutes || '');

            // Set channels
            setSelectedChannels(parsedData.selectedChannels || []);

            // Set variables
            if (parsedData.variables && Array.isArray(parsedData.variables)) {
              setVariables(parsedData.variables);

              // Set variable sample values
              const sampleValues = {};
              parsedData.variables.forEach(v => {
                sampleValues[v.name] = v.description;
              });
              setVariableSampleValues(sampleValues);
            }

            if (parsedData.whatsappBody) {
              setWhatsappBody(parsedData.whatsappBody);
            }
            if (parsedData.whatsappHeader) {
              setWhatsappHeader({
                type: parsedData.whatsappHeader.type,
                content: parsedData.whatsappHeader.content || parsedData.whatsappHeader.headerText || '',
                file: parsedData.whatsappHeader.fileUrl || '',
                document_filename: parsedData.whatsappHeader.document_filename || '',
                footerText: parsedData.whatsappHeader.footerText,
                latitude: parsedData.whatsappHeader.latitude,
                location_address: parsedData.whatsappHeader.location_address,
                location_name: parsedData.whatsappHeader.location_name,
                longitude: parsedData.whatsappHeader.longitude,
              });
            }
            if (parsedData.whatsappFooter) {
              setWhatsappFooter(parsedData.whatsappFooter);
            }
            if (parsedData.deep_link) {
              setPushDeepLink(parsedData.deep_link);
            }
            if (parsedData.whatsappButtons && Array.isArray(parsedData.whatsappButtons)) {
              setWhatsappButtons(parsedData.whatsappButtons.map(btn => {
                if (btn.type !== 'custom-link') return btn;
                if (btn.urlType === 'embed-web-stories' && btn.webStoriesUrl) {
                  const { cleanUrl, utmEnabled, utmParams } = parseUtmFromUrl(btn.webStoriesUrl);
                  return { ...btn, webStoriesUrl: cleanUrl, utmEnabled, utmParams };
                }
                if ((btn.urlType === 'static' || btn.urlType === 'dynamic') && btn.url) {
                  const { cleanUrl, utmEnabled, utmParams } = parseUtmFromUrl(btn.url);
                  return { ...btn, url: cleanUrl, utmEnabled, utmParams };
                }
                return btn;
              }));
              if (parsedData.whatsappButtons.some(b => b.urlType === 'embed-web-stories')) fetchWebStories();
            }
            if (parsedData.enable_tracker !== undefined) {
              setEnableTracker(parsedData.enable_tracker);
            }
            // Accept whichever truthy shape the API returns (1/'1'/true/'Y'), so a
            // stored "N" is not read as on the way a bare truthiness check would.
            if (parsedData.chargeable !== undefined) {
              setChargeable(isFlagOn(parsedData.chargeable) ? 1 : 0);
            }
            if (parsedData.opt_in_out !== undefined) {
              setOptInOut(isFlagOn(parsedData.opt_in_out) ? 1 : 0);
            }
            if (parsedData.isExpiryEnabled !== undefined) {
              setWhatsappMessageExpiryEnabled(parsedData.isExpiryEnabled);
            }
            if (parsedData.expiry_time !== undefined) {
              setWhatsappMessageExpiry(parsedData.expiry_time.toString());
            }

            // Set In-App data
            if (parsedData.inAppBody) {
              setInAppBody(parsedData.inAppBody);
            }
            if (parsedData.inAppHeader) {
              setInAppHeader({
                type: parsedData.inAppHeader.type || 'text',
                content: parsedData.inAppHeader.content || parsedData.inAppHeader.headerText || '',
                file: parsedData.inAppHeader.fileUrl || ''
              });
            }
            if (parsedData.inAppFooter) {
              setInAppFooter(parsedData.inAppFooter);
            }
            if (parsedData.inAppButtons && Array.isArray(parsedData.inAppButtons)) {
              setInAppButtons(parsedData.inAppButtons);
              if (parsedData.inAppButtons.some(b => b.urlType === 'embed-web-stories')) fetchWebStories();
            }
            if (parsedData.inAppMessageExpiry !== undefined) {
              setInAppMessageExpiry(parsedData.inAppMessageExpiry.toString());
              setInAppMessageExpiryEnabled(parsedData.inAppMessageExpiry > 0);
            }

            // Set SMS data
            if (parsedData.smsBody) {
              setSmsBody(parsedData.smsBody);
            }

            // Set Push data
            if (parsedData.pushTitle) {
              setPushTitle(parsedData.pushTitle);
            }
            if (parsedData.pushBody) {
              setPushBody(parsedData.pushBody);
            }
            if (parsedData.pushImage) {

              // Handle both string and object formats
              if (typeof parsedData.pushImage === 'string') {
                setPushImage(parsedData.pushImage);
              } else if (parsedData.pushImage.url) {
                setPushImage(parsedData.pushImage.url);
              }
            }

            // Set Email data
            if (parsedData.emailSubject) {
              setEmailSubject(parsedData.emailSubject);
            }
            if (parsedData.emailType) {
              setEmailType(parsedData.emailType);
            }
            if (parsedData.emailHtml) {
              setEmailHtml(parsedData.emailHtml);
            }
            if (parsedData.html_code) {
              setEmailCode(parsedData.html_code);
            }
            if (parsedData.emailTemplate) {
              setEmailTemplate(parsedData.emailTemplate);
            }
            if (parsedData.email_template_id) {
              setEmailTemplateId(parsedData.email_template_id);
            }

            // Set Voice data
            if (parsedData.voiceScript) {
              setVoiceScript(parsedData.voiceScript);
            }
            if (parsedData.voiceRetries) {
              setVoiceRetries(parsedData.voiceRetries);
            }
            if (parsedData.voiceCallType) {
              setVoiceCallType(parsedData.voiceCallType);
            }
            if (parsedData.voiceCallId) {
              setVoiceCallId(parsedData.voiceCallId);
            }
            if (parsedData.voiceCallIds && Array.isArray(parsedData.voiceCallIds)) {
              setVoiceCallIds(parsedData.voiceCallIds);
            }
            if (parsedData.voiceVariableSource) {
              setVoiceVariableSource(parsedData.voiceVariableSource);
            }
            if (parsedData.ivr_variables && Array.isArray(parsedData.ivr_variables)) {
              setIvrVariables(parsedData.ivr_variables);
            }

            // Set OTP data if template type is OTP
            if (parsedData.otpType) {
              setOtpType(parsedData.otpType);
            }
            if (parsedData.otpApps && Array.isArray(parsedData.otpApps)) {
              setOtpApps(parsedData.otpApps);
            }
            if (parsedData.authAddSecurityRecommendation !== undefined) {
              setAuthAddSecurityRecommendation(parsedData.authAddSecurityRecommendation);
            }
            if (parsedData.authAddExpirationTime !== undefined) {
              setAuthAddExpirationTime(parsedData.authAddExpirationTime);
            }

            // Set Events - Convert from API format to component format
            if (parsedData.events && Array.isArray(parsedData.events)) {
              const eventsForComponent = parsedData.events.map((eventObj, index) => {
                // Check if it's already in component format or API format
                if (eventObj.eventName !== undefined) {
                  // Already in component format
                  return eventObj;
                } else {
                  // API format - convert to component format
                  const eventName = Object.keys(eventObj)[0];
                  const eventData = eventObj[eventName];

                  return {
                    id: `event_${Date.now()}_${index}`,
                    eventName: eventName,
                    fixedValue: eventData.fixed_value || '',
                    dynamicValue: eventData.dynamic_value || []
                  };
                }
              });

              setEvents(eventsForComponent);
            }

            toast({
              title: "Template Loaded",
              description: "Template data loaded successfully",
              status: "success",
              duration: 2000,
              isClosable: true,
            });

          } catch (parseError) {
            console.error('Error parsing message_combine:', parseError);
            toast({
              title: "Parse Error",
              description: "Failed to parse template data",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error loading template data:', error);
      toast({
        title: "Load Error",
        description: error.response?.data?.message || "Failed to load template data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };





  // Prefill the OTP body on every text channel when the type is Authentication.
  //
  // Editing an existing template never runs this — the saved bodies win.
  useEffect(() => {
    if (isEdit) return;

    const isOtp = templateType === '1';
    const otpText = authAddSecurityRecommendation ? OTP_BODY_WITH_SECURITY : OTP_BODY;

    // Only ever fill a channel that is actually ENABLED. Writing the body regardless left
    // a $otp behind on a switched-off channel, and the Variables panel reported it.
    // Clearing is the mirror image: only ever discard an UNTOUCHED prefill, so a body the
    // author wrote survives disabling the channel or changing the type.
    const seed = (value, setValue, enabled, locked = false) => {
      if (isOtp && enabled) {
        if (locked || !String(value || '').trim() || isOtpBody(value)) setValue(otpText);
      } else if (isOtpBody(value)) {
        setValue('');
      }
    };

    // WhatsApp is `locked` — the field is read-only for Authentication, so it always
    // tracks the approved copy rather than being merely seeded.
    seed(whatsappBody, setWhatsappBody, selectedChannels.includes(CHANNEL.WHATSAPP), true);
    seed(smsBody, setSmsBody, selectedChannels.includes(CHANNEL.SMS));
    seed(pushBody, setPushBody, selectedChannels.includes(CHANNEL.PUSH));
    seed(inAppBody, setInAppBody, selectedChannels.includes(CHANNEL.IN_APP));
    seed(voiceScript, setVoiceScript, selectedChannels.includes(CHANNEL.VOICE));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateType, authAddSecurityRecommendation, selectedChannels]);

  // Authentication templates carry a fixed, Meta-approved OTP body — there is no
  // marketing copy to give or withhold consent over, so bypassing consent is not a
  // meaningful choice for them. Force it off whenever the type is (or becomes)
  // Authentication, including in edit mode: an existing template saved with the flag
  // on before this rule existed should not keep it.
  useEffect(() => {
    if (templateType === '1' && optInOut !== 0) setOptInOut(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateType]);

  useEffect(() => {
    if (isEdit) return;
    const firstOption = MESSAGE_EXPIRY_OPTIONS[templateType]?.[0]?.value;
    if (firstOption !== undefined) setWhatsappMessageExpiry(firstOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateType]);

  useEffect(() => {
    // The prefill used to be duplicated here, unconditionally — it wrote the OTP body on
    // mount even for a non-Authentication type. The effect above already covers mount
    // (templateType defaults to '1') and does it type-aware, so this only loads the list.
    LoadMessageTemplateType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const LoadMessageTemplateType = async () => {

    try {
      const url = `${process.env.REACT_APP_API_URL}/message-type/list`;
      const data = await fetchWrapper.get(url);
      setMessageTemplateTypeList(data.data || []);
    } catch (error) {
      console.error('Error loading template types:', error);

    }
  };

  useEffect(() => {
    const channelVars = extractAllChannelVariables();


    const newVariables = channelVars.map(varName => {
      // const existing = variables.find(v => v.name === varName);

      return { name: varName, description: variableSampleValues[varName] };
    });

    const hasChanged =
      newVariables.length !== variables.length ||
      newVariables.some(nv => !variables.find(v => v.name === nv.name));

    if (hasChanged) {
      setVariables(newVariables);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // BUG-03: selectedChannels must be a dependency too — toggling a channel changes which
    // bodies are scanned, and without it the saved `variables` (what Send Test reads)
    // stayed frozen at the first channel's set.
  }, [selectedChannels, whatsappBody, inAppBody, smsBody, pushTitle, pushBody, deeplink, emailSubject, emailHtml, voiceScript, whatsappButtons, inAppButtons]);

  const extractVariablesFromContent = (content) => {
    const matches = content.match(/\$[a-zA-Z_][a-zA-Z0-9_]*/g);
    if (!matches) return [];
    return [...new Set(matches.map(match => match.slice(1)))];
  };

  /**
   * Variables from ENABLED channels only.
   *
   * This used to scan every body unconditionally, which contradicted the panel's own
   * heading ("detected across all enabled channels"): a body belonging to a channel the
   * author had switched off still contributed variables, and they then showed up as
   * "Inconsistent" against the channels that ARE on. The Authentication prefill made it
   * obvious — $otp appeared from the disabled WhatsApp body even after the SMS copy had
   * been changed to a different variable.
   *
   * Dynamic button URLs are now scoped to their own channel too, instead of both button
   * sets being read whether or not WhatsApp / In-App were enabled.
   */
  // Every text field that can hold variables, labelled by where it lives. Both the
  // variable list and its "Used in" tags read from this one list, so a variable can
  // never be detected without also showing where it was found — e.g. a $search that
  // only exists in a WhatsApp dynamic button URL shows "WhatsApp (Button URL)".
  const getVariableSources = () => {
    const dynamicUrls = (buttons) => (buttons || [])
      .filter(btn => btn.urlType === 'dynamic' && btn.url)
      .map(btn => btn.url)
      .join(' ');
    const on = (channel) => selectedChannels.includes(channel);
    const sources = [];

    if (on(CHANNEL.WHATSAPP)) {
      sources.push({ label: 'WhatsApp', text: whatsappBody });
      sources.push({ label: 'WhatsApp (Button URL)', text: dynamicUrls(whatsappButtons) });
    }
    if (on(CHANNEL.SMS)) sources.push({ label: 'SMS', text: smsBody });
    if (on(CHANNEL.PUSH)) {
      sources.push({ label: 'Push', text: `${pushTitle || ''} ${pushBody || ''}` });
      sources.push({ label: 'Push (Deep Link)', text: deeplink });
    }
    if (on(CHANNEL.IN_APP)) {
      sources.push({ label: 'Inbox', text: inAppBody });
      sources.push({ label: 'Inbox (Button URL)', text: dynamicUrls(inAppButtons) });
    }
    if (on(CHANNEL.VOICE)) sources.push({ label: 'Voice', text: voiceScript });
    if (on(CHANNEL.EMAIL)) sources.push({ label: 'Email', text: `${emailSubject || ''} ${emailHtml || ''}` });

    return sources.map(s => ({ ...s, vars: extractVariablesFromContent(s.text || '') }));
  };

  const extractAllChannelVariables = () => {
    const allVariables = new Set();
    getVariableSources().forEach(s => s.vars.forEach(v => allVariables.add(v)));
    return Array.from(allVariables);
  };

  // Channels (and sub-fields like button URLs / deep link) that use this variable.
  const getVariableChannelUsage = (variableName) =>
    getVariableSources()
      .filter(s => s.vars.includes(variableName))
      .map(s => ({ channel: s.label, present: true }));

  // BUG-01/02/04: each channel owns its own variables. The old "Inconsistent" check flagged
  // any variable not present in EVERY enabled channel — so just switching on an empty
  // channel, or using $name in WhatsApp and $customer_name in SMS, was reported as an
  // error. Variables are now the union across enabled channels, each tagged with the
  // channels that actually use it; there is no cross-channel consistency requirement.
  const allChannelVariables = extractAllChannelVariables();

  const updateVariableSampleValue = (varName, sampleValue) => {
    setVariableSampleValues(prev => ({ ...prev, [varName]: sampleValue }));
    setVariables(prev => prev.map(v =>
      v.name === varName ? { ...v, description: sampleValue } : v
    ));
  };

  const handleChannelToggle = (channelId) => {


    setSelectedChannels(prev => {
      const isCurrentlySelected = prev.includes(channelId);

      if (isCurrentlySelected) {
        // If unchecking, remove this channel
        return prev.filter(id => id !== channelId);
      } else {
        // If checking, add both in-app and push
        const newChannels = [...prev, channelId];

        // Auto-select the other channel
        if (channelId === 'In-App Notification' && !prev.includes('Push Notification')) {
          newChannels.push('Push Notification');
        } else if (channelId === 'Push Notification' && !prev.includes('In-App Notification')) {
          newChannels.push('In-App Notification');
        }

        return newChannels;
      }
    });
  };

  const handleWhatsAppHeaderTypeChange = (type) => {
    setWhatsappHeader({ type, content: '', file: '' });
  };

  const handleInAppHeaderTypeChange = (type) => {
    setInAppHeader({ type, content: '', file: '' });
  };

  const uploadFile = async (file) => {
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/template-message/media-upload`,
        formDataUpload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-access-token": localStorage.getItem("customer-portal"),
          },
        }
      );

      const result = response.data;

      if (result.success) {
        toast({
          title: "Success",
          description: result.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        return result.fileUrl;
      } else {
        throw new Error(result.message || "Upload failed");
      }
    } catch (error) {
      toast({
        title: "Upload Error",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (event, target) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileUrl = await uploadFile(file);
      if (fileUrl) {
        if (target === 'whatsapp-header') {
          setWhatsappHeader(prev => ({ ...prev, file: fileUrl }));
        } else if (target === 'inapp-header') {
          setInAppHeader(prev => ({ ...prev, file: fileUrl }));
        } else if (target === 'push-image') {
          setPushImage(fileUrl);
        }
      }
    }
  };

  const removeImage = (target) => {
    if (target === 'whatsapp-header') {
      setWhatsappHeader(prev => ({ ...prev, file: '' }));
    } else if (target === 'inapp-header') {
      setInAppHeader(prev => ({ ...prev, file: '' }));
    } else if (target === 'push-image') {
      setPushImage('');
    }
  };

  const addWhatsAppButton = () => {
    if (whatsappButtons.length < 3) {
      const newButton = {
        id: `btn_${Date.now()}`,
        type: 'custom-link',
        name: '',
        countryCode: '+1',
        urlType: 'static'
      };
      setWhatsappButtons([...whatsappButtons, newButton]);
    }
  };

  const updateWhatsAppButton = (id, updates) => {
    setWhatsappButtons(prev => prev.map(btn =>
      btn.id === id ? { ...btn, ...updates } : btn
    ));
  };

  const removeWhatsAppButton = (id) => {
    setWhatsappButtons(prev => prev.filter(btn => btn.id !== id));
  };

  const addInAppButton = () => {
    if (inAppButtons.length < 3) {
      const newButton = {
        id: `btn_${Date.now()}`,
        type: 'custom-link',
        name: '',
        countryCode: '+1',
        urlType: 'static'
      };
      setInAppButtons([...inAppButtons, newButton]);
    }
  };

  const updateInAppButton = (id, updates) => {
    setInAppButtons(prev => prev.map(btn =>
      btn.id === id ? { ...btn, ...updates } : btn
    ));
  };

  const removeInAppButton = (id) => {
    setInAppButtons(prev => prev.filter(btn => btn.id !== id));
  };

  // Add this helper function near the top of your component
  const getTabIndexForChannel = (channelName) => {
    return availableChannels.findIndex(ch => ch.name === channelName);
  };
  const syncWhatsAppToInApp = () => {
    setInAppHeader(whatsappHeader);
    setInAppFooter(whatsappFooter);
    setInAppBody(whatsappBody)
    setInAppButtons([...whatsappButtons]);
    setTabIndex(3)
    toast({
      title: "Success",
      description: "All settings are synced to Inbox",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const syncInAppToWhatsApp = () => {
    setWhatsappHeader(inAppHeader);
    setWhatsappFooter(inAppFooter);
    setWhatsappBody(inAppBody)
    setWhatsappButtons([...inAppButtons]);
    setTabIndex(0)
    toast({
      title: "Success",
      description: "All Settings are sync to Whatsapp",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const addOtpApp = () => {
    if (otpApps.length < 5) {
      setOtpApps([...otpApps, { package_name: '', signature_hash: '' }]);
    }
  };

  const updateOtpApp = (index, field, value) => {
    const updated = [...otpApps];
    updated[index][field] = value;
    setOtpApps(updated);
  };

  const removeOtpApp = (index) => {
    if (otpApps.length > 1) {
      setOtpApps(otpApps.filter((_, i) => i !== index));
    }
  };

  const addEvent = () => {
    const newEvent = {
      id: `event_${Date.now()}`,
      eventName: '',
      dynamicValue: [],
      fixedValue: ''
    };
    setEvents([...events, newEvent]);
  };

  const updateEvent = (id, updates) => {
    setEvents(prev => prev.map(event =>
      event.id === id ? { ...event, ...updates } : event
    ));
  };

  const removeEvent = (id) => {
    setEvents(prev => prev.filter(event => event.id !== id));
  };

  const updateEventDynamicValue = (eventId, variableName, isSelected) => {
    setEvents(prev => prev.map(event => {
      if (event.id === eventId) {
        const currentValues = event.dynamicValue || [];
        if (isSelected && !currentValues.includes(variableName)) {
          return { ...event, dynamicValue: [...currentValues, variableName] };
        } else if (!isSelected && currentValues.includes(variableName)) {
          return { ...event, dynamicValue: currentValues.filter(val => val !== variableName) };
        }
      }
      return event;
    }));
  };

  const getCharacterCount = (text, limit) => {
    const count = text.length;
    const isOverLimit = count > limit;
    return (
      <Text fontSize="xs" color={isOverLimit ? 'red.500' : 'gray.500'}>
        {count}/{limit} characters {isOverLimit && '(limit exceeded)'}
      </Text>
    );
  };

  const handleSaveTemplate = async () => {
    // Validation
    if (!templateName.trim()) {
      toast({
        title: "Validation Error",
        description: "Template name is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(templateName)) {
      toast({
        title: "Validation Error",
        description: "Template name can only contain letters, numbers, and underscores.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!templateType) {
      toast({
        title: "Validation Error",
        description: "Template type is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (selectedChannels.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one channel",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate variables have sample values
    if (allChannelVariables.length > 0) {
      const missingDescriptions = allChannelVariables.some(
        varName => !variableSampleValues[varName] || !variableSampleValues[varName].trim()
      );
      if (missingDescriptions) {
        toast({
          title: "Validation Error",
          description: "Please provide sample values for all variables",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    // Validate events - ALL fields required if event is added
    if (events.length > 0) {
      for (let i = 0; i < events.length; i++) {
        const event = events[i];

        // Event name is required
        if (!event.eventName || !event.eventName.trim()) {
          toast({
            title: "Event Validation Error",
            description: `Event ${i + 1}: Event name is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        // Either fixed value or dynamic values must be provided
        const hasFixedValue = event.fixedValue && event.fixedValue.trim() !== "";
        const hasDynamicValues = event.dynamicValue && event.dynamicValue.length > 0;

        if (!hasFixedValue && !hasDynamicValues) {
          toast({
            title: "Event Validation Error",
            description: `Event ${i + 1}: Either fixed value or at least one dynamic value is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }
      }
    }

    // Validate WhatsApp specific fields
    if (selectedChannels.includes('Whatsapp')) {
      // Body is always required
      if ((!whatsappBody || !whatsappBody.trim()) && templateType !== '1') {
        toast({
          title: "WhatsApp Validation",
          description: "WhatsApp message body is required",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(0);
        return;
      }

      if (whatsappBody.length > 1024) {
        toast({
          title: "WhatsApp Validation",
          description: "WhatsApp message body cannot be more than 1024 characters",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(0);
        return;
      }

      if (countEmojis(whatsappBody) > 10) {
        toast({
          title: "WhatsApp Validation",
          description: "WhatsApp message body cannot contain more than 10 emojis",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(0);
        return;
      }

      // Header validation
      if (whatsappHeader.type === 'text' && templateType !== '1') {
        if ((!whatsappHeader.content || !whatsappHeader.content.trim()) && templateType !== '1') {
          toast({
            title: "WhatsApp Validation",
            description: "Header text is required when header type is text",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(0);
          return;
        }
      }

      if ((whatsappHeader.type === 'image' && !whatsappHeader.file) && templateType !== '1') {
        toast({
          title: "WhatsApp Validation",
          description: "Image is required when header type is image",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(0);
        return;
      }

      if ((whatsappHeader.type === 'video' && !whatsappHeader.file) && templateType !== '1') {
        toast({
          title: "WhatsApp Validation",
          description: "Video is required when header type is video",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(0);
        return;
      }

      if ((whatsappHeader.type === 'document' && !whatsappHeader.document_filename?.trim()) && templateType !== '1') {
        toast({
          title: "WhatsApp Validation",
          description: "Document filename is required when header type is document",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(0);
        return;
      }

      if ((whatsappHeader.type === 'document' && !whatsappHeader.file) && templateType !== '1') {
        toast({
          title: "WhatsApp Validation",
          description: "Document is required when header type is document",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(0);
        return;
      }

      // Validate buttons
      for (let i = 0; i < whatsappButtons.length; i++) {
        const button = whatsappButtons[i];

        if ((!button.name || !button.name.trim()) && templateType !== '1' && button.type !== 'copy-offer') {
          toast({
            title: "WhatsApp Button Validation",
            description: `Button ${i + 1}: Button text is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(0);
          return;
        }

        if (!button.type && templateType !== '1') {
          toast({
            title: "WhatsApp Button Validation",
            description: `Button ${i + 1}: Button type is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(0);
          return;
        }

        if (button.type === 'call-me' && (!button.phoneNumber || !button.phoneNumber.trim()) && templateType !== '1') {
          toast({
            title: "WhatsApp Button Validation",
            description: `Button ${i + 1}: Phone number is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(0);
          return;
        }

        if (button.type === 'custom-link' && button.urlType !== 'embed-web-stories' && (!button.url || !button.url.trim()) && templateType !== '1') {
          toast({
            title: "WhatsApp Button Validation",
            description: `Button ${i + 1}: URL is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(0);
          return;
        }

        if (button.type === 'custom-link' && button.urlType === 'embed-web-stories' && (!button.webStoriesUrl || !button.webStoriesUrl.trim()) && templateType !== '1') {
          toast({
            title: "WhatsApp Button Validation",
            description: `Button ${i + 1}: Web Stories URL is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(0);
          return;
        }
      }
    }

    // Validate In-App specific fields
    if (selectedChannels.includes('In-App Notification')) {
      // Body is always required
      if (!inAppBody || !inAppBody.trim()) {
        toast({
          title: "Inbox Validation",
          description: "Inbox message body is required",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(3);
        return;
      }

      // Header validation
      if (inAppHeader.type === 'text' && templateType !== '1') {
        if (!inAppHeader.content || !inAppHeader.content.trim()) {
          toast({
            title: "Inbox Validation",
            description: "Header text is required when header type is text",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(3);
          return;
        }
      }

      if ((inAppHeader.type === 'image' && !inAppHeader.file) && templateType !== '1') {
        toast({
          title: "Inbox Validation",
          description: "Image is required when header type is image",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(3);
        return;
      }

      if ((inAppHeader.type === 'video' && !inAppHeader.file) && templateType !== '1') {
        toast({
          title: "Inbox Validation",
          description: "Video is required when header type is video",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(3);
        return;
      }

      if ((inAppHeader.type === 'document' && !inAppHeader.file) && templateType !== '1') {
        toast({
          title: "Inbox Validation",
          description: "Document is required when header type is document",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(3);
        return;
      }

      // Validate buttons
      for (let i = 0; i < inAppButtons.length; i++) {
        const button = inAppButtons[i];

        if ((!button.name || !button.name.trim()) && templateType !== '1' && button.type !== 'copy-offer') {
          toast({
            title: "Inbox Button Validation",
            description: `Button ${i + 1}: Button text is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(3);
          return;
        }

        if (!button.type && templateType !== '1') {
          toast({
            title: "Inbox Button Validation",
            description: `Button ${i + 1}: Button type is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(3);
          return;
        }

        if (button.type === 'call-me' && (!button.phoneNumber || !button.phoneNumber.trim()) && templateType !== '1') {
          toast({
            title: "Inbox Button Validation",
            description: `Button ${i + 1}: Phone number is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(3);
          return;
        }

        if (button.type === 'custom-link' && button.urlType !== 'embed-web-stories' && (!button.url || !button.url.trim()) && templateType !== '1') {
          toast({
            title: "Inbox Button Validation",
            description: `Button ${i + 1}: URL is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(3);
          return;
        }

        if (button.type === 'custom-link' && button.urlType === 'embed-web-stories' && (!button.webStoriesUrl || !button.webStoriesUrl.trim()) && templateType !== '1') {
          toast({
            title: "Inbox Button Validation",
            description: `Button ${i + 1}: Web Stories URL is required`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(3);
          return;
        }
      }
    }

    // Validate SMS specific fields
    if (selectedChannels.includes('SMS')) {
      if (!smsBody || !smsBody.trim()) {
        toast({
          title: "SMS Validation",
          description: "SMS message is required",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(1);
        return;
      }
    }

    // Validate Push Notification specific fields
    if (selectedChannels.includes('Push Notification')) {
      if (!pushTitle || !pushTitle.trim()) {
        toast({
          title: "Push Notification Validation",
          description: "Notification title is required",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(2);
        return;
      }

      if (!pushBody || !pushBody.trim()) {
        toast({
          title: "Push Notification Validation",
          description: "Notification body is required",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(2);
        return;
      }
    }

    // Validate Voice specific fields
    if (selectedChannels.includes('Voice (IVR)')) {
      if (voiceCallType === 'text_to_voice' && (!voiceScript || !voiceScript.trim())) {
        toast({
          title: "Voice Validation",
          description: "Voice script is required",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(4);
        return;
      }
      if (voiceCallType === 'recorded_call' && (!voiceCallIds || voiceCallIds.length === 0)) {
        toast({
          title: "Voice Validation",
          description: "Call ID is required for recorded call",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(4);
        return;
      }
    }

    // Validate Email specific fields
    if (selectedChannels.includes('E-Mail')) {
      if (!emailSubject || !emailSubject.trim()) {
        toast({
          title: "Email Validation",
          description: "Email subject is required",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        setTabIndex(5);
        return;
      }

      if (emailType === 'email_template') {
        // Content lives in the referenced Email Designer record, not hand-typed here —
        // just require that a template was actually picked, skip the length check below.
        if (!emailTemplateId) {
          toast({
            title: "Email Validation",
            description: "Please select an email template",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(5);
          return;
        }
      } else {
        const emailContent = emailType === 'html_code' ? emailCode : emailHtml;
        if (!emailContent || !emailContent.trim()) {
          toast({
            title: "Email Validation",
            description: "Email content is required",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setTabIndex(5);
          return;
        }

        if (emailContent.length > 65000) {
          toast({
            title: "Email Validation",
            description: `Email content exceeds the 65,000 character limit (currently ${emailContent.length.toLocaleString()} characters). Please reduce the content before saving.`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setTabIndex(5);
          return;
        }
      }
    }

    setSaving(true);

    try {
      // Convert events to API format
      const eventsForAPI = events.map(event => ({
        [event.eventName]: {
          fixed_value: event.fixedValue || '',
          dynamic_value: event.dynamicValue || []
        }
      }));

      // Prepare the payload matching the logged structure
      const templateData = {
        // Basic Information
        templateName,
        templateType,
        description,
        customer_id: selectedCustomer || JSON.parse(localStorage.getItem("customer"))?.customer_id,

        // Master Message Content
        masterMessageContent: whatsappBody, // or use a separate masterMessageContent state

        // Variables — built from the live enabled-channel scan rather than the `variables`
        // state, so a save right after toggling a channel can't send a stale list (BUG-03).
        variables: allChannelVariables.map(name => ({
          name,
          description: variableSampleValues[name] || ''
        })),

        // Channels
        selectedChannels,
        selectedChannelIds: selectedChannels.map(channelName => {
          const channel = availableChannels.find(ch => ch.name === channelName);
          return channel?.id;
        }).filter(Boolean),

        // WhatsApp Configuration
        whatsappBody,
        whatsappHeader: {
          type: whatsappHeader.type,
          content: whatsappHeader.content,
          fileUrl: whatsappHeader.file || '',
          document_filename: whatsappHeader.document_filename || '',
          mediaType: whatsappHeader.type,
          headerText: whatsappHeader.content,
          footerText: whatsappFooter,
          ...(whatsappHeader.type === 'location' && {
            location_name: whatsappHeader.location_name || '',
            location_address: whatsappHeader.location_address || '',
            latitude: whatsappHeader.latitude || '',
            longitude: whatsappHeader.longitude || ''
          })
        },
        whatsappFooter,
        enable_tracker: enableTracker,
        whatsappButtons: whatsappButtons.map(btn => ({
          id: btn.id,
          type: btn.type,
          name: btn.name,
          countryCode: btn.countryCode,
          phoneNumber: btn.phoneNumber,
          url: btn.urlType === 'embed-web-stories' ? undefined : buildUrlWithUtm(btn.url, btn.utmEnabled, btn.utmParams),
          urlType: btn.urlType,
          webStoriesUrl: btn.urlType === 'embed-web-stories' ? buildUrlWithUtm(btn.webStoriesUrl, btn.utmEnabled, btn.utmParams) : undefined,
          duration: btn.duration,
          unit: btn.unit,
          offerCode: btn.offerCode,
        })),
        whatsappTemplateName: templateName,
        isExpiryEnabled: whatsappMessageExpiryEnabled,
        expiry_time: whatsappMessageExpiryEnabled ? parseInt(whatsappMessageExpiry) : 0,

        // In-App Configuration
        inAppBody,
        inAppHeader: {
          type: inAppHeader.type,
          content: inAppHeader.content,
          fileUrl: inAppHeader.file || '',
          mediaType: inAppHeader.type,
          headerText: inAppHeader.content,
          footerText: inAppFooter,
        },
        inAppFooter,
        inAppButtons: inAppButtons.map(btn => ({
          id: btn.id,
          type: btn.type,
          name: btn.name,
          countryCode: btn.countryCode,
          phoneNumber: btn.phoneNumber,
          url: btn.url,
          urlType: btn.urlType,
          webStoriesUrl: btn.urlType === 'embed-web-stories' ? btn.webStoriesUrl : undefined,
          duration: btn.duration,
          unit: btn.unit,
          offerCode: btn.offerCode,
        })),
        inAppMessageExpiry: inAppMessageExpiryEnabled ? parseInt(inAppMessageExpiry) : 0,

        // SMS Configuration
        smsBody,

        // Push Notification Configuration
        pushTitle,
        pushBody,
        pushImage: pushImage ? { url: pushImage } : {},
        deep_link: deeplink, // Add if you have this field

        // Email Configuration
        emailSubject,
        emailType,
        emailHtml,
        html_code: emailCode,
        emailTemplate,
        email_template_id: emailType === 'email_template' ? emailTemplateId : null,

        // Voice Configuration
        voiceScript: voiceCallType === 'text_to_voice' ? voiceScript : '',
        voiceRetries: voiceRetries || '',
        voiceCallType,
        voiceCallIds: voiceCallType === 'recorded_call' ? voiceCallIds : [],
        voiceVariableSource: voiceCallType === 'recorded_call' ? voiceVariableSource : '',
        ivr_variables: voiceCallType === 'recorded_call' && voiceVariableSource === 'custom'
          ? ivrVariables.map(v => typeof v === 'string' ? v : (v?.key || '')).filter(v => v.trim())
          : allChannelVariables,

        // OTP Configuration (if template type is OTP)
        ...(templateType === '1' && {
          otpType,
          otpApps: otpApps.filter(app => app.package_name && app.signature_hash),
          authAddSecurityRecommendation,
          authAddExpirationTime
        }),

        // Events
        events: eventsForAPI,

        // Billing / consent flags
        chargeable,
        opt_in_out: optInOut,

        // Language
        lang: lang || 'en',
        authCodeExpiryMinutes: authCodeExpiryMinutes
      };



      const url = isEdit
        ? `${process.env.REACT_APP_API_URL}/template-message/update-combine`
        : `${process.env.REACT_APP_API_URL}/template-message/create-combine`;

      const payload = isEdit ? { id, ...templateData } : templateData;
      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("customer-portal"),
          "x-api-key": localStorage.getItem("api-key"),
        },
      });

      if (response.data?.success) {

        toast({
          title: `Template ${isEdit ? "updated" : "created"} successfully`,
          description: response.data.message || `Template ${isEdit ? "updated" : "created"} successfully`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        navigate(-1)

      } else {
        toast.error(response.data?.message || "Failed to save template");
      }

    } catch (error) {
      console.error("Save error:", error);
      // toast.error(error.response?.data?.error || error.response?.data?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const normalizeUrl = (url) => {
    if (!url) return '';
    try {
      const [base, qs] = url.split('?');
      const remaining = qs ? qs.split('&').filter(p => !p.startsWith('utm_')) : [];
      const clean = remaining.length > 0 ? `${base}?${remaining.join('&')}` : base;
      return clean.replace(/\/+$/, '').toLowerCase();
    } catch { return url.replace(/\/+$/, '').toLowerCase(); }
  };

  const parseUtmFromUrl = (rawUrl) => {
    if (!rawUrl) return { cleanUrl: rawUrl, utmEnabled: false, utmParams: {} };
    try {
      const [base, qs] = rawUrl.split('?');
      if (!qs) return { cleanUrl: rawUrl, utmEnabled: false, utmParams: {} };
      const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'];
      const utmParams = {};
      const remaining = [];
      qs.split('&').forEach(pair => {
        const [k, v] = pair.split('=');
        const key = UTM_KEYS.find(uk => k === `utm_${uk}`);
        if (key) {
          utmParams[key] = { enabled: true, value: decodeURIComponent(v || '') };
        } else {
          remaining.push(pair);
        }
      });
      const hasUtm = Object.keys(utmParams).length > 0;
      const cleanUrl = remaining.length > 0 ? `${base}?${remaining.join('&')}` : base;
      return { cleanUrl, utmEnabled: hasUtm, utmParams };
    } catch {
      return { cleanUrl: rawUrl, utmEnabled: false, utmParams: {} };
    }
  };

  const buildUrlWithUtm = (url, utmEnabled, utmParams) => {
    if (!utmEnabled || !url || !utmParams) return url || '';
    const pairs = ['source', 'medium', 'campaign', 'term', 'content']
      .filter(k => utmParams[k]?.enabled && utmParams[k]?.value?.trim())
      .map(k => `utm_${k}=${encodeURIComponent(utmParams[k].value.trim())}`);
    if (pairs.length === 0) return url;
    return url + (url.includes('?') ? '&' : '?') + pairs.join('&');
  };

  // isInbox: Inbox buttons are always "Visit Website" and have no Meta link tracking,
  // so the Button Type selector and the tracking checkbox are hidden there.
  const renderButtonConfiguration = (buttons, updateButton, removeButton, maxButtons, buttonLabel, showUtm = false, isInbox = false) => {
    if (buttons.length === 0) {
      return (
        <Box textAlign="center" py={8} color="gray.500">
          <Text>No buttons added yet. Click "Add Button" to create interactive buttons.</Text>
        </Box>
      );
    }

    const countryCodeOptions = COUNTRY_CODES.map(c => ({
      value: c.iso,           // unique key (e.g. 'US', 'CA', 'IN')
      label: `${c.name} (${c.code})`,
      iso: c.iso,
      name: c.name,
      code: c.code,           // dial code (e.g. '+1', '+91')
    }));

    return (
      <VStack spacing={4}>
        {buttons.map((button, index) => (
          <Card key={button.id} w="full" bg="white" borderWidth="1px" borderColor="gray.200">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" fontWeight="medium">{buttonLabel} {index + 1}</Text>
                  <IconButton
                    aria-label="Remove button"
                    icon={<FiTrash2 />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => removeButton(button.id)}
                  />
                </Flex>

                <Grid templateColumns={{ base: '1fr', md: isInbox ? '1fr' : 'repeat(2, 1fr)' }} gap={4}>
                  {!isInbox && (
                  <FormControl>
                    <FormLabel fontSize="sm">Button Type</FormLabel>
                    <Select
                      value={button.type}
                      onChange={(e) => updateButton(button.id, { type: e.target.value })}
                      size="sm"
                    >
                      <option value="quick-reply">Quick Reply</option>
                      <option value="custom-link">Visit Website</option>
                      <option value="call-me">Call Phone Number</option>
                      <option value="voice-call">Call On Whatsapp</option>
                      {templateTypes.find(t => String(t.id) === String(templateType))?.name?.toLowerCase().includes('marketing') && (
                        <option value="copy-offer">Copy Offer</option>
                      )}

                    </Select>
                  </FormControl>
                  )}
                  <FormControl>
                    <FormLabel fontSize="sm">Button Text</FormLabel>
                    <Input
                      value={button.type === 'copy-offer' ? 'Copy offer code' : button.name}
                      onChange={(e) => updateButton(button.id, { name: e.target.value })}
                      placeholder="Enter button text"
                      maxLength={25}
                      size="sm"
                      isDisabled={button.type === 'copy-offer'}
                    />
                    {button.type !== 'copy-offer' && (
                      <Flex justify="flex-end" mt={1}>
                        {getCharacterCount(button.name, 25)}
                      </Flex>
                    )}
                  </FormControl>


                </Grid>

                {button.type === 'custom-link' && (
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <FormLabel fontSize="sm" display="flex" alignItems="center" gap={2}>
                        <FiGlobe />
                        URL Type
                      </FormLabel>
                      <VStack spacing={2} align="stretch">
                        <HStack spacing={2}>
                          <input
                            type="radio"
                            id={`static-${button.id}`}
                            name={`url-type-${button.id}`}
                            value="static"
                            checked={button.urlType === 'static'}
                            onChange={() => updateButton(button.id, { urlType: 'static' })}
                          />
                          <FormLabel htmlFor={`static-${button.id}`} mb={0} fontSize="sm" display="flex" alignItems="center" gap={2}>
                            {isInbox ? 'Custom URL' : 'Static URL'}
                          </FormLabel>
                        </HStack>
                        <HStack spacing={2}>
                          <input
                            type="radio"
                            id={`dynamic-${button.id}`}
                            name={`url-type-${button.id}`}
                            value="dynamic"
                            checked={button.urlType === 'dynamic'}
                            onChange={() => updateButton(button.id, { urlType: 'dynamic' })}
                          />
                          <FormLabel htmlFor={`dynamic-${button.id}`} mb={0} fontSize="sm" display="flex" alignItems="center" gap={2}>
                            Dynamic URL (with variables)
                          </FormLabel>
                        </HStack>
                        <HStack spacing={2}>
                          <input
                            type="radio"
                            id={`embed-web-stories-${button.id}`}
                            name={`url-type-${button.id}`}
                            value="embed-web-stories"
                            checked={button.urlType === 'embed-web-stories'}
                            onChange={() => { updateButton(button.id, { urlType: 'embed-web-stories' }); fetchWebStories(); }}
                          />
                          <FormLabel htmlFor={`embed-web-stories-${button.id}`} mb={0} fontSize="sm" display="flex" alignItems="center" gap={2}>
                            Embed Web Stories
                          </FormLabel>
                        </HStack>
                        <HStack spacing={2}>
                          <input
                            type="radio"
                            id={`deeplink-${button.id}`}
                            name={`url-type-${button.id}`}
                            value="deeplink"
                            checked={button.urlType === 'deeplink'}
                            onChange={() => updateButton(button.id, { urlType: 'deeplink' })}
                          />
                          <FormLabel htmlFor={`deeplink-${button.id}`} mb={0} fontSize="sm" display="flex" alignItems="center" gap={2}>
                            Deep Link
                          </FormLabel>
                        </HStack>
                      </VStack>
                    </Box>

                    {button.urlType === 'deeplink' && (
                      <FormControl>
                        <FormLabel fontSize="sm">Deep Link</FormLabel>
                        <DeepLinkSelect
                          value={button.url}
                          onPick={(e) => updateButton(button.id, { url: e.web_fallback_url || e.android_link || e.ios_link })}
                        />
                        {button.url && <Text fontSize="xs" color="gray.500" mt={1}>Selected: {button.url}</Text>}
                      </FormControl>
                    )}

                    {button.urlType !== 'embed-web-stories' && button.urlType !== 'deeplink' && <FormControl isInvalid={button.urlType === 'dynamic' && (button.url?.match(/\$[a-zA-Z_][a-zA-Z0-9_]*/g) || []).length > 1}>
                      <FormLabel fontSize="sm">
                        {button.urlType === 'dynamic' ? 'URL Template' : 'URL'}
                      </FormLabel>
                      <Input
                        value={button.url || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          const varMatches = val.match(/\$[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
                          if (button.urlType === 'dynamic' && varMatches.length > 1) return;
                          updateButton(button.id, { url: val });
                        }}
                        placeholder={
                          button.urlType === 'dynamic'
                            ? "e.g., https://example.com/verify?code=$code"
                            : "e.g., https://example.com/verify"
                        }
                        size="sm"
                      />
                      {button.urlType === 'dynamic' && (
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Use $variableName to insert one variable (e.g. $code)
                        </Text>
                      )}
                    </FormControl>}

                    {button.urlType === 'embed-web-stories' && (() => {
                      const storyOptions = webStoriesList.map(s => ({
                        value: s.storyMetadata?.publishedUrl || s.storyMetadata?.cloudflareUrl || '',
                        label: s.title,
                      }));

                      const cleanButtonUrl = normalizeUrl(button.webStoriesUrl);
                      const matchedStory = storyOptions.find(o => normalizeUrl(o.value) === cleanButtonUrl);

                      // If not found in list yet, inject as provisional option
                      if (cleanButtonUrl && !matchedStory) {
                        storyOptions.unshift({ value: cleanButtonUrl, label: cleanButtonUrl });
                      }

                      const selectedStory = cleanButtonUrl
                        ? (matchedStory || { value: cleanButtonUrl, label: cleanButtonUrl })
                        : null;

                      return (
                        <VStack spacing={2} align="stretch">
                          <FormControl>
                            <FormLabel fontSize="sm">Select Web Story</FormLabel>
                            <ReactSelect
                              isLoading={webStoriesLoading}
                              options={storyOptions}
                              value={selectedStory}
                              onChange={(opt) => updateButton(button.id, { webStoriesUrl: opt ? opt.value : '' })}
                              placeholder="Select a story..."
                              isClearable
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                container: (b) => ({ ...b, fontSize: '14px' }),
                                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                              }}
                            />
                          </FormControl>
                        </VStack>
                      );
                    })()}

                    {/* UTM Parameters — static URL and embed-web-stories only */}
                    {showUtm && (button.urlType === 'static' || button.urlType === 'embed-web-stories') && (() => {
                      const UTM_FIELDS = [
                        { key: 'source', label: 'Source', placeholder: 'e.g. google' },
                        { key: 'medium', label: 'Medium', placeholder: 'e.g. cpc, email' },
                        { key: 'campaign', label: 'Campaign', placeholder: 'e.g. summer_sale' },
                        { key: 'term', label: 'Term', placeholder: 'e.g. keyword' },
                        { key: 'content', label: 'Content', placeholder: 'e.g. banner_top' },
                      ];
                      const baseUrl = button.urlType === 'embed-web-stories' ? button.webStoriesUrl : button.url;
                      const activeCount = UTM_FIELDS.filter(f => button.utmParams?.[f.key]?.enabled && button.utmParams[f.key]?.value?.trim()).length;
                      const previewUrl = button.utmEnabled && baseUrl ? buildUrlWithUtm(baseUrl, true, button.utmParams) : null;

                      return (
                        <Box mt={3} borderWidth="1px" borderColor={button.utmEnabled ? 'blue.200' : 'gray.200'} borderRadius="lg" overflow="hidden">

                          {/* Header toggle */}
                          <Flex
                            px={4} py={3}
                            bg={button.utmEnabled ? 'blue.50' : 'gray.50'}
                            align="center"
                            justify="space-between"
                            cursor="pointer"
                            borderBottom={button.utmEnabled ? '1px solid' : 'none'}
                            borderColor="blue.100"
                            onClick={() => updateButton(button.id, { utmEnabled: !button.utmEnabled })}
                          >
                            <HStack spacing={2}>
                              <FiTarget size={14} color={button.utmEnabled ? 'var(--brand-primary)' : '#9ca3af'} />
                              <Text fontSize="sm" fontWeight="semibold" color={button.utmEnabled ? 'blue.700' : 'gray.500'}>
                                UTM Tracking
                              </Text>
                              {button.utmEnabled && activeCount > 0 && (
                                <Badge colorScheme="blue" variant="subtle" fontSize="xs" borderRadius="full" px={2}>
                                  {activeCount} / {UTM_FIELDS.length} set
                                </Badge>
                              )}
                            </HStack>
                            <Switch
                              size="sm"
                              isChecked={button.utmEnabled || false}
                              colorScheme="blue"
                              onChange={(e) => { e.stopPropagation(); updateButton(button.id, { utmEnabled: e.target.checked }); }}
                            />
                          </Flex>

                          {button.utmEnabled && (
                            <Box p={3} bg="white">
                              <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                                {UTM_FIELDS.map(({ key, label, placeholder }) => {
                                  const param = button.utmParams?.[key] || { enabled: false, value: '' };
                                  return (
                                    <Box
                                      key={key}
                                      p={3}
                                      borderWidth="1px"
                                      borderColor={param.enabled ? 'blue.200' : 'gray.200'}
                                      borderRadius="md"
                                      bg={param.enabled ? 'blue.50' : 'white'}
                                      transition="background 0.15s, border-color 0.15s"
                                    >
                                      <Checkbox
                                        isChecked={param.enabled}
                                        onChange={(e) => updateButton(button.id, {
                                          utmParams: { ...button.utmParams, [key]: { ...param, enabled: e.target.checked, value: e.target.checked ? param.value : '' } },
                                        })}
                                        colorScheme="blue"
                                        size="sm"
                                        mb={param.enabled ? 2 : 0}
                                      >
                                        <Text fontSize="sm" fontWeight={param.enabled ? 'semibold' : 'normal'} color={param.enabled ? 'blue.700' : 'gray.600'}>
                                          {label}
                                        </Text>
                                      </Checkbox>
                                      {param.enabled && (
                                        <Input
                                          size="sm"
                                          placeholder={placeholder}
                                          value={param.value || ''}
                                          bg="white"
                                          borderColor="blue.200"
                                          _focus={{ borderColor: 'var(--brand-primary)', boxShadow: '0 0 0 1px var(--brand-primary)' }}
                                          onChange={(e) => updateButton(button.id, {
                                            utmParams: { ...button.utmParams, [key]: { ...param, value: e.target.value } },
                                          })}
                                        />
                                      )}
                                    </Box>
                                  );
                                })}
                              </Grid>

                              {previewUrl && (
                                <Box mt={3} p={3} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                                  <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1} textTransform="uppercase" letterSpacing="wide">
                                    Final URL
                                  </Text>
                                  <Text fontSize="xs" color="gray.700" wordBreak="break-all" lineHeight="tall">
                                    {previewUrl}
                                  </Text>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </VStack>
                )}

                {button.type === 'call-me' && (
                  <VStack spacing={3} align="stretch">
                    <Grid templateColumns={{ base: '1fr', md: '250px 1fr' }} gap={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Country Code</FormLabel>
                        <ReactSelect
                          options={countryCodeOptions}
                          value={
                            countryCodeOptions.find(opt => opt.value === button.countryCodeIso) ||
                            countryCodeOptions.find(opt => opt.code === button.countryCode) ||
                            null
                          }
                          onChange={(selected) => {
                            if (selected) {
                              updateButton(button.id, {
                                countryCode: selected.code,
                                countryCodeIso: selected.value,
                              });
                            }
                          }}
                          placeholder="Search country..."
                          isSearchable
                          filterOption={(option, inputValue) => {
                            const q = inputValue.toLowerCase();
                            return (
                              option.data.name.toLowerCase().includes(q) ||
                              option.data.code.includes(q) ||
                              option.data.iso.toLowerCase().includes(q)
                            );
                          }}
                          formatOptionLabel={(option, { context }) => {
                            if (context === 'value') {
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                  <span style={{ color: '#718096' }}>{option.iso}</span>
                                  <span>{option.code}</span>
                                </div>
                              );
                            }
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                                <span style={{ color: '#718096', minWidth: '28px' }}>{option.iso}</span>
                                <span>{option.name}</span>
                                <span style={{ color: '#A0AEC0', marginLeft: 'auto', fontFamily: 'monospace' }}>{option.code}</span>
                              </div>
                            );
                          }}
                          styles={{
                            control: (base) => ({ ...base, minHeight: '32px', fontSize: '14px', borderColor: '#E2E8F0' }),
                            valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                            input: (base) => ({ ...base, margin: 0, padding: 0 }),
                            indicatorSeparator: () => ({ display: 'none' }),
                            dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
                            option: (base, state) => ({
                              ...base, fontSize: '13px', padding: '6px 12px',
                              backgroundColor: state.isSelected ? 'var(--brand-primary)' : state.isFocused ? 'var(--brand-tint)' : 'white',
                              color: state.isSelected ? 'white' : '#1A202C',
                            }),
                            menu: (base) => ({ ...base, zIndex: 9999 }),
                            menuList: (base) => ({ ...base, maxHeight: '200px' }),
                            singleValue: (base) => ({ ...base, fontSize: '13px' }),
                          }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm">Phone Number</FormLabel>
                        <Input
                          value={button.phoneNumber || ''}
                          onChange={(e) => updateButton(button.id, { phoneNumber: e.target.value })}
                          placeholder="Enter phone number without country code"
                          size="sm"
                        />
                      </FormControl>
                    </Grid>
                    {/* <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" px={3} py={2}>
                      <Text fontSize="xs" color="blue.700">
                        <strong>Note:</strong> Enter the phone number digits only, without the country code. Ensure the number is reachable for voice calls.
                      </Text>
                    </Box> */}
                  </VStack>
                )}

                {button.type === 'voice-call' && (
                  <FormControl>
                    <FormLabel fontSize="sm">Active For (Days)</FormLabel>
                    <Select
                      value={button.duration || ''}
                      onChange={(e) => updateButton(button.id, { duration: Number(e.target.value), unit: 'DAYS' })}
                      size="sm"
                      placeholder="Select number of days"
                    >
                      {Array.from({ length: 30 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day} {day === 1 ? 'Day' : 'Days'}</option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {button.type === 'copy-offer' && (
                  <FormControl>
                    <FormLabel fontSize="sm">Offer Code</FormLabel>
                    <Input
                      value={button.offerCode || ''}
                      onChange={(e) => {
                        if (e.target.value.length <= 20) {
                          updateButton(button.id, { offerCode: e.target.value });
                        }
                      }}
                      placeholder="Enter offer code (max 20 characters)"
                      maxLength={20}
                      size="sm"
                    />
                    <Flex justify="flex-end" mt={1}>
                      <Text fontSize="xs" color={(button.offerCode?.length || 0) >= 20 ? 'red.500' : 'gray.500'}>
                        {button.offerCode?.length || 0}/20
                      </Text>
                    </Flex>
                  </FormControl>
                )}
              </VStack>
            </CardBody>
          </Card>
        ))}
        {!isInbox && buttons.some(b => b.type === 'custom-link') &&
          (
            <Box bg="gray.100" borderRadius="md" px={4} py={3} alignSelf="flex-start">
              <Checkbox
                isChecked={enableTracker === 1}
                onChange={(e) => setEnableTracker(e.target.checked ? 1 : 0)}
                size="sm"
              >
                <Text fontSize="sm" color="gray.600">You direct Meta to use link tracking to report website clicks. When this box is unticked, you will not see insights for website clicks.</Text>
              </Checkbox>
            </Box>
          )}
      </VStack>
    );
  };
  const isTemplateApproved = (whatsappStatusId === 1 && (templateDataGlobal.message_type_id === templateDataGlobal?.wa_message_type_id));

  if (permissionsLoading) {
    return (
      <>
        <Header />
        <Center h="400px"><Spinner size="xl" color="blue.500" thickness="4px" /></Center>
      </>
    );
  }
  if (!xPermission) {
    return (
      <>
        <Header />
        <Center h="400px">
          <Box textAlign="center">
            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>No Access</Text>
            <Text fontSize="sm" color="gray.500">You don't have permission to view this sections.</Text>
          </Box>
        </Center>
      </>
    );
  }

  return (
    <>
      <Header />

      <BoxWrapper >
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center">
            <Box>
              <Heading size={"lg"} >{isEdit ? "Edit Template" : "Create New Template"}</Heading>
              <Text color="gray.600">Configure multi-channel message template</Text>
            </Box>
            <Flex gap={2} align="center">
              {/* ET_041: "Send Test Email" intentionally NOT offered here. Sending a
                  test belongs to the Email Designer, where there is a finished email
                  to send; on Create/Edit Template the content may not exist yet. */}
              <Button leftIcon={<FiSave />} size={"sm"} bg="var(--brand-primary)" colorScheme="blue" onClick={handleSaveTemplate} disabled={saving} isLoading={saving}>
                {isEdit ? "Update Template" : "Save Template"}
              </Button>
            </Flex>
          </Flex>

          {/* Basic Information */}
          <Box bg="white" className='box-style-card'>
            <Box>
              <Text fontSize={"lg"} fontWeight={"600"}>Basic Information</Text>
            </Box>
            <Box>
              <VStack spacing={4} align="stretch">
                <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                  <FormControl isRequired isInvalid={templateName.length > 0 && !/^[a-zA-Z0-9_]+$/.test(templateName)}>
                    <FormLabel>Template Name</FormLabel>
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value.replace(/ /g, '_'))}
                      placeholder="Enter template name"
                    />
                    <FormErrorMessage>
                      Only letters, numbers, and underscores are allowed.
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Template Type</FormLabel>
                    <Select
                      value={templateType}
                      onChange={(e) => {
                        setTemplateType(e.target.value);
                        setOtpType('');
                      }}
                      placeholder="Select template type"
                      disabled={isEdit}
                    >
                      {templateTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Language</FormLabel>
                    <ReactSelect
                      options={languageOptions}
                      value={languageOptions.find(opt => opt.value === lang) || null}
                      onChange={(selected) => setLang(selected?.value || 'en')}
                      placeholder="Search language..."
                      isSearchable
                      isDisabled={isEdit}
                      filterOption={(option, inputValue) => {
                        const q = inputValue.toLowerCase();
                        return (
                          option.data.label.toLowerCase().includes(q) ||
                          option.data.value.toLowerCase().includes(q)
                        );
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '40px',
                          borderColor: '#E2E8F0',
                        }),
                        indicatorSeparator: () => ({ display: 'none' }),
                        option: (base, state) => ({
                          ...base,
                          fontSize: '14px',
                          backgroundColor: state.isSelected ? 'var(--brand-primary)' : state.isFocused ? 'var(--brand-tint)' : 'white',
                          color: state.isSelected ? 'white' : '#1A202C',
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menuList: (base) => ({
                          ...base,
                          maxHeight: '200px',
                        }),
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </FormControl>
                </Grid>


                <FormControl>
                  <FormLabel>Description (Optional)</FormLabel>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of this template"
                    rows={2}
                  />
                </FormControl>

                {/* Sent with the template as chargeable / opt_in_out */}
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  <FormControl>
                    <Flex
                      align="center"
                      justify="space-between"
                      gap={3}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      px={4}
                      py={3}
                    >
                      <Box>
                        <FormLabel htmlFor="template-chargeable" mb={0} fontSize="sm">
                          Chargeable
                        </FormLabel>
                        <Text fontSize="xs" color="gray.500">
                          Messages sent with this template are billed to customer
                        </Text>
                      </Box>
                      <Switch
                        id="template-chargeable"
                        size="sm"
                        colorScheme="green"
                        isChecked={chargeable === 1}
                        onChange={(e) => setChargeable(e.target.checked ? 1 : 0)}
                      />
                    </Flex>
                  </FormControl>

                  <FormControl isDisabled={templateType === '1'}>
                    <Flex
                      align="center"
                      justify="space-between"
                      gap={3}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      px={4}
                      py={3}
                      opacity={templateType === '1' ? 0.6 : 1}
                    >
                      <Box>
                        <FormLabel htmlFor="template-opt-in-out" mb={0} fontSize="sm">
                          Bypass Customer Consent
                        </FormLabel>
                        <Text fontSize="xs" color="gray.500">
                          {templateType === '1'
                            ? 'Not applicable to Authentication templates'
                            : 'Customer Consent will be bypassed if this check is turned on'}
                        </Text>
                      </Box>
                      <Switch
                        id="template-opt-in-out"
                        size="sm"
                        colorScheme="green"
                        isChecked={optInOut === 1}
                        isDisabled={templateType === '1'}
                        onChange={(e) => setOptInOut(e.target.checked ? 1 : 0)}
                      />
                    </Flex>
                  </FormControl>
                </Grid>
              </VStack>
            </Box>
          </Box>

          {/* Channel Configuration using ChannelTabsFixed */}
          <ChannelTabsFixed
            selectedChannels={selectedChannels}
            setLang={setLang}
            lang={lang}
            setPushDeepLink={setPushDeepLink}
            deeplink={deeplink}
            setAuthCodeExpiryMinutes={setAuthCodeExpiryMinutes}
            authCodeExpiryMinutes={authCodeExpiryMinutes}
            availableChannels={availableChannels}
            setTabIndex={setTabIndex}
            tabIndex={tabIndex}
            isTemplateApproved={isTemplateApproved}
            // selectedChannels={selectedChannels}
            handleChannelToggle={handleChannelToggle}
            whatsappBody={whatsappBody}
            setWhatsappBody={setWhatsappBody}
            whatsappMessageExpiry={whatsappMessageExpiry}
            setWhatsappMessageExpiry={setWhatsappMessageExpiry}
            whatsappMessageExpiryEnabled={whatsappMessageExpiryEnabled}
            setWhatsappMessageExpiryEnabled={setWhatsappMessageExpiryEnabled}
            whatsappHeader={whatsappHeader}
            setWhatsappHeader={setWhatsappHeader}
            whatsappFooter={whatsappFooter}
            setWhatsappFooter={setWhatsappFooter}
            whatsappButtons={whatsappButtons}
            addWhatsAppButton={addWhatsAppButton}
            updateWhatsAppButton={updateWhatsAppButton}
            removeWhatsAppButton={removeWhatsAppButton}
            handleWhatsAppHeaderTypeChange={handleWhatsAppHeaderTypeChange}
            handleFileUpload={handleFileUpload}
            removeImage={removeImage}
            uploading={uploading}
            pushImage={pushImage}
            templateType={templateType}
            otpType={otpType}
            setOtpType={setOtpType}
            otpApps={otpApps}
            addOtpApp={addOtpApp}
            updateOtpApp={updateOtpApp}
            removeOtpApp={removeOtpApp}
            inAppBody={inAppBody}
            setInAppBody={setInAppBody}
            inAppMessageExpiry={inAppMessageExpiry}
            setInAppMessageExpiry={setInAppMessageExpiry}
            inAppMessageExpiryEnabled={inAppMessageExpiryEnabled}
            setInAppMessageExpiryEnabled={setInAppMessageExpiryEnabled}
            inAppHeader={inAppHeader}
            setInAppHeader={setInAppHeader}
            inAppFooter={inAppFooter}
            setInAppFooter={setInAppFooter}
            inAppButtons={inAppButtons}
            addInAppButton={addInAppButton}
            updateInAppButton={updateInAppButton}
            removeInAppButton={removeInAppButton}
            handleInAppHeaderTypeChange={handleInAppHeaderTypeChange}
            smsBody={smsBody}
            setSmsBody={setSmsBody}
            pushTitle={pushTitle}
            setPushTitle={setPushTitle}
            pushBody={pushBody}
            setPushBody={setPushBody}
            setPushImage={setPushImage}
            emailSubject={emailSubject}
            setEmailSubject={setEmailSubject}
            emailType={emailType}
            setEmailType={setEmailType}
            emailHtml={emailHtml}
            setEmailHtml={setEmailHtml}
            emailCode={emailCode}
            setEmailCode={setEmailCode}
            emailTemplate={emailTemplate}
            setEmailTemplate={setEmailTemplate}
            emailTemplateId={emailTemplateId}
            setEmailTemplateId={setEmailTemplateId}
            voiceScript={voiceScript}
            setVoiceScript={setVoiceScript}
            voiceRetries={voiceRetries}
            setVoiceRetries={setVoiceRetries}
            voiceCallType={voiceCallType}
            setVoiceCallType={setVoiceCallType}
            voiceCallId={voiceCallId}
            setVoiceCallId={setVoiceCallId}
            voiceCallIds={voiceCallIds}
            setVoiceCallIds={setVoiceCallIds}
            voiceVariableSource={voiceVariableSource}
            setVoiceVariableSource={setVoiceVariableSource}
            ivrVariables={ivrVariables}
            setIvrVariables={setIvrVariables}
            allChannelVariables={allChannelVariables}
            getCharacterCount={getCharacterCount}
            renderButtonConfiguration={renderButtonConfiguration}
            enableTracker={enableTracker}
            setEnableTracker={setEnableTracker}
            templateTypes={templateTypes}
            // messageExpiryOptions={messageExpiryOptions}
            syncWhatsAppToInApp={syncWhatsAppToInApp}
            syncInAppToWhatsApp={syncInAppToWhatsApp}
            setAuthAddSecurityRecommendation={setAuthAddSecurityRecommendation}
            setAuthAddExpirationTime={setAuthAddExpirationTime}
            authAddExpirationTime={authAddExpirationTime}
            authAddSecurityRecommendation={authAddSecurityRecommendation}
          />

          {/* Variables Section */}
          {selectedChannels.length > 0 && allChannelVariables.length > 0 && (
            <Box className='box-style-card'>
              <Box>
                <HStack spacing={2}>
                  <FiDollarSign size={20} color="orange" />
                  <Text fontSize={"lg"} fontWeight={"600"}>Variables</Text>
                </HStack>
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Variables detected across all enabled channels
                </Text>
              </Box>
              <Box>
                <VStack spacing={3} align="stretch">
                  {allChannelVariables.map((variableName) => {
                    // Only the channels that actually use this variable — a channel without
                    // it is not an error (BUG-01/02/04).
                    const usedIn = getVariableChannelUsage(variableName).filter(u => u.present);

                    return (
                      <Box
                        key={variableName}
                        p={4}
                        mt={4}
                        borderWidth="1px"
                        borderRadius={"8.45px"}
                        borderColor="gray.200"
                        bg="transparent"
                      >
                        <Grid templateColumns={{ base: '1fr', lg: '200px 1fr' }} gap={4}>
                          <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={2}>Variable</Text>
                            <HStack spacing={2} mb={2}>
                              <Tag bg="transparent" borderWidth="1px" borderColor="#0000001a" fontSize="xs">
                                ${variableName}
                              </Tag>
                            </HStack>

                            <Text fontSize="xs" color="gray.500" mt={2} mb={1}>Used in</Text>
                            <Flex flexWrap="wrap" gap={1}>
                              {usedIn.map(({ channel }) => {
                                const color =
                                  channel.startsWith('WhatsApp') ? 'green' :
                                    channel === 'SMS' ? 'blue' :
                                      channel === 'Email' ? 'red' :
                                        channel.startsWith('Push') ? 'orange' :
                                          channel.startsWith('Inbox') ? 'cyan' :
                                            channel === 'Voice' ? 'purple' : 'gray';
                                return (
                                  <Tag key={channel} bg={color} borderColor={color} variant="solid" fontSize="2xs">
                                    {channel}
                                  </Tag>
                                );
                              })}
                            </Flex>
                          </Box>

                          <FormControl>
                            <FormLabel fontSize="sm">Sample Value</FormLabel>
                            <Input
                              value={variableSampleValues[variableName] || ''}
                              onChange={(e) => updateVariableSampleValue(variableName, e.target.value)}
                              placeholder={`Enter sample value for $${variableName}...`}
                              size="sm"
                              bg={"white"}
                            />
                          </FormControl>
                        </Grid>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            </Box>
          )}

          {/* Event Capturing */}
          <Box className='box-style-card'>
            <Box>
              <HStack spacing={2}>
                <FiActivity size={20} color="purple" />
                <Text fontSize={"lg"} fontWeight={"600"}>Event Capturing</Text>
              </HStack>
              <Text fontSize="sm" color="gray.600" mt={1}>
                Configure events to capture when this template is used
              </Text>
            </Box>
            <Box>
              <VStack spacing={4} align="stretch">
                <Flex justify="space-between" align="center">
                  <span></span>
                  {events.length === 0 && (
                    <Button
                      size="sm"
                      leftIcon={<FiPlus />}
                      onClick={addEvent}
                      variant="outline"
                    >
                      Add Event
                    </Button>
                  )}
                </Flex>

                {events.length === 0 ? (
                  <Box textAlign="center" py={8} color="gray.500">
                    <FiTarget size={32} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
                    <Text>No events configured yet</Text>
                  </Box>
                ) : (
                  <VStack spacing={4}>
                    {events.map((event) => (
                      <Box
                        key={event.id}
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        w="full"
                        bg="white"
                      >
                        <VStack spacing={4} align="stretch">
                          <Flex justify="space-between">
                            <Text fontWeight="semibold">Events</Text>
                            <IconButton
                              aria-label="Remove event"
                              icon={<FiTrash2 />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => removeEvent(event.id)}
                            />
                          </Flex>

                          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                            <FormControl isRequired>
                              <FormLabel fontSize="sm">Event Name</FormLabel>
                              <Input
                                value={event.eventName}
                                onChange={(e) => updateEvent(event.id, { eventName: e.target.value })}
                                placeholder="e.g., OTP_Sent"
                                size="sm"
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="sm">Dynamic Values</FormLabel>
                              <Box
                                borderWidth="1px"
                                borderRadius="md"
                                p={2}
                                maxH="120px"
                                overflowY="auto"
                              >
                                {variables.length > 0 ? (
                                  <VStack spacing={2} align="stretch">
                                    {variables.map((variable, index) => (
                                      <Checkbox
                                        key={variable.name || index}
                                        isChecked={event.dynamicValue?.includes(variable.name) || false}
                                        onChange={(e) =>
                                          updateEventDynamicValue(event.id, variable.name, e.target.checked)
                                        }
                                        size="sm"
                                      >
                                        <Text fontSize="sm"  >
                                          ${variable.name}
                                        </Text>
                                      </Checkbox>
                                    ))}
                                  </VStack>
                                ) : (
                                  <Text fontSize="xs" color="gray.500">
                                    No variables available
                                  </Text>
                                )}
                              </Box>
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="sm">Fixed Value</FormLabel>
                              <Input
                                value={event.fixedValue}
                                onChange={(e) => updateEvent(event.id, { fixedValue: e.target.value })}
                                placeholder="Enter fixed value"
                                size="sm"
                              />
                            </FormControl>
                          </Grid>

                          {event.dynamicValue && event.dynamicValue.length > 0 && (
                            <Box>
                              <Text fontSize="xs" color="gray.600" mb={1}>Selected Variables:</Text>
                              <Flex flexWrap="wrap" gap={1}>
                                {event.dynamicValue.map((varName) => (
                                  <Tag key={varName} border="1px solid #E2E8F0" fontSize="xs" bg="transparent">
                                    ${varName}
                                  </Tag>
                                ))}
                              </Flex>
                            </Box>
                          )}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </VStack>
            </Box>
          </Box>
        </VStack>
      </BoxWrapper>

    </>
  );
}